import * as React from 'react';

import {
  enumLoadStatus,
  enumSalesLoadLineItemPhase,
} from '@rematter/frontend-lib-api-client';
import { Button, cn, Footer } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import { LucidePlusCircle } from 'lucide-react';

import { BrokeredLoadRequestedTable } from '../../_shared/LineItems';

import { LoadRequestedMaterialsFooter } from './LoadRequestedMaterialsFooter';

import {
  $brokeredLoadForm,
  $salesOrderLineItems,
} from '~/modules/brokerage/_state/brokeredLoadDetail.state';
import { useBrokeredLoadLineItemsManager } from '~/modules/brokerage/hooks/useBrokeredLoadLineItemsManager';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import {
  BrokeredLoadFormState,
  BrokeredLoadRecord,
} from '~/modules/brokerage/types/brokeredLoad';
import { WeightUnitSwitch } from '~/modules/trade/_components/_shared/WeightUnitSwitch/WeightUnitSwitch';
import { WeightUnitType } from '~/modules/trade/types/load';

type Props = {
  load: BrokeredLoadRecord;
  onDiscard?: () => void;
  onSaveUpdates?: () => void;
  isSaving?: boolean;
};

const loadMaterialsFormSelector = (state: BrokeredLoadFormState) => ({
  lineItems: state.lineItems,
});

export const LoadRequestedMaterials: React.FC<Props> = ({
  load,
  onDiscard,
  onSaveUpdates,
  isSaving = false,
}) => {
  const t = useTranslations();
  const [weightUnit, setWeightUnit] = React.useState<WeightUnitType>('price');
  const selectedFormAtom = React.useMemo(
    () => selectAtom($brokeredLoadForm, loadMaterialsFormSelector, isEqual),
    [],
  );
  const form = useAtomValue(selectedFormAtom);

  const salesOrderLineItemsQuery = useAtomValue($salesOrderLineItems);
  const salesOrderLineItems = salesOrderLineItemsQuery.data ?? [];

  const {
    addItem,
    updateItem,
    deleteItem,
    hasUnaddedSalesOrderItems,
    addLineItemsFromSalesOrder,
  } = useBrokeredLoadLineItemsManager({ salesOrderLineItems });

  const isEditable =
    load.status === enumLoadStatus.Pending ||
    load.status === enumLoadStatus.Unassigned;

  const requestedItems = React.useMemo(
    () =>
      form.lineItems.filter(
        (item) =>
          !item.phase || item.phase === enumSalesLoadLineItemPhase.Requested,
      ),
    [form.lineItems],
  );

  const handleAddRequestedItem = React.useCallback(() => {
    addItem({ phase: enumSalesLoadLineItemPhase.Requested });
  }, [addItem]);

  return (
    <>
      <div className="flex-1 overflow-auto px-5 py-6 pr-4">
        <div className="mb-2">
          <WeightUnitSwitch value={weightUnit} onChange={setWeightUnit} />
        </div>
        <BrokeredLoadRequestedTable
          lineItems={requestedItems}
          hideFooter
          onAddLineItem={handleAddRequestedItem}
          onUpdateLineItem={updateItem}
          onDeleteLineItem={deleteItem}
          weightUnit={weightUnit}
        />

        {isEditable && (
          <div
            className={cn(
              'mt-4 flex gap-3',
              !requestedItems.length && 'justify-center',
            )}
          >
            <Button
              variant="outline"
              color="neutral"
              icon={<LucidePlusCircle />}
              onPress={handleAddRequestedItem}
            >
              {t.brokerage.loads.detail.materials.addMaterial}
            </Button>
            {hasUnaddedSalesOrderItems && (
              <Button
                variant="outline"
                color="neutral"
                icon={<LucidePlusCircle />}
                onPress={addLineItemsFromSalesOrder}
              >
                {t.brokerage.loads.detail.materials.addAllFromSalesOrder}
              </Button>
            )}
          </div>
        )}
      </div>

      <Footer className="flex-shrink-0" displayShadow>
        <div className="flex items-center justify-end gap-5 px-5 py-2.5">
          <LoadRequestedMaterialsFooter
            lineItems={requestedItems}
            weightUnit={weightUnit}
          />

          <div className="ml-4 flex items-center justify-end gap-5">
            <Button
              color="neutral"
              variant="outline"
              onPress={() => onDiscard?.()}
              isDisabled={isSaving}
            >
              {t.brokerage.loads.detail.footer.discard}
            </Button>
            <Button
              color="primary"
              variant="solid"
              onPress={() => onSaveUpdates?.()}
              isPending={isSaving}
              isLoading={isSaving}
            >
              {t.brokerage.loads.detail.footer.saveUpdates}
            </Button>
          </div>
        </div>
      </Footer>
    </>
  );
};
